<template>
    <div class="month-worktype-chart-wrap">
        <div class="time-progress-title" v-if="title">{{ title }}</div>
        <div class="time-progress-title-sub text-muted" v-if="subtitle">{{ subtitle }}</div>
        <div v-if="hasHours" class="month-worktype-chart-canvas" @mouseleave="onMouseLeave">
            <doughnut :key="chartKey" :data="chartData" :options="monthChartOptions"/>
            <div class="month-worktype-chart-center">
                <div class="month-worktype-chart-total">{{ formatLoggedHours(totalHours) }}</div>
                <div class="month-worktype-chart-total-label">hrs</div>
            </div>
            <div class="chartjs-custom-tooltip" style="opacity: 0; pointer-events: none;"></div>
        </div>
        <div v-else class="month-worktype-chart-empty text-muted">
            {{ emptyText }}
        </div>
    </div>
</template>

<script>
    import _ from "lodash";
    import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
    import { Doughnut } from "vue-chartjs";
    import workTypeChartMixin from "../mixinWorkTypeChart";

    const zoomFixPlugin = {
        id: "chartZoomFix",
        beforeEvent(chart, args) {
            const native = args.event && args.event.native;
            if (!native || !chart.canvas) {
                return;
            }
            const zoom = (typeof document !== "undefined" && parseFloat(document.documentElement.style.zoom)) || 1;
            if (zoom !== 1) {
                const rect = chart.canvas.getBoundingClientRect();
                const clientX = native.touches && native.touches.length ? native.touches[0].clientX : native.clientX;
                const clientY = native.touches && native.touches.length ? native.touches[0].clientY : native.clientY;
                if (clientX !== undefined && clientY !== undefined) {
                    args.event.x = Math.round((clientX - rect.left) / zoom);
                    args.event.y = Math.round((clientY - rect.top) / zoom);
                    args.inChartArea = chart.isPointInArea(args.event);
                }
            }
        }
    };

    ChartJS.register(ArcElement, Tooltip, Legend, zoomFixPlugin);

    export default {
        mixins: [workTypeChartMixin],
        components: {
            doughnut: Doughnut
        },
        props: {
            hours: {
                type: Array,
                default: () => []
            },
            title: {
                type: String,
                default: ""
            },
            subtitle: {
                type: String,
                default: ""
            },
            emptyText: {
                type: String,
                default: "No logged hours this month"
            }
        },
        computed: {
            hasHours() {
                return _.some(this.hours, t => Number(t.hours) > 0);
            },
            totalHours() {
                return _.sumBy(this.hours, t => Number(t.hours) || 0);
            },
            chartData() {
                return this.getWorkTypePieData(this.hours);
            },
            chartKey() {
                return `${JSON.stringify(this.hours || [])}-${this.isDarkMode ? 'dark' : 'light'}`;
            }
        },
        methods: {
            onMouseLeave() {
                const tooltipEl = this.$el && this.$el.querySelector(".chartjs-custom-tooltip");
                if (tooltipEl) {
                    tooltipEl.style.opacity = "0";
                    tooltipEl.style.pointerEvents = "none";
                }
                const centerEl = this.$el && this.$el.querySelector(".month-worktype-chart-center");
                if (centerEl) {
                    centerEl.classList.remove("is-hidden");
                }
            }
        },
        beforeUnmount() {
            const tooltipEl = this.$el && this.$el.querySelector(".chartjs-custom-tooltip");
            if (tooltipEl && tooltipEl.parentNode) {
                tooltipEl.parentNode.removeChild(tooltipEl);
            }
        }
    };
</script>

<style scoped>
    .month-worktype-chart-wrap {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        height: 100%;
    }
    .time-progress-title {
        font-size: 0.78rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #6c757d;
        margin-bottom: 0.55rem;
        text-align: center;
    }
    .time-progress-title-sub {
        text-align: center;
        font-size: 0.75rem;
        margin-bottom: 0.55rem;
    }
    .month-worktype-chart-canvas {
        position: relative;
        height: 188px;
        max-height: 188px;
    }
    .month-worktype-chart-center {
        position: absolute;
        top: 38%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
        pointer-events: none;
        z-index: 1;
        transition: opacity 0.15s ease;
    }
    .month-worktype-chart-center.is-hidden {
        opacity: 0 !important;
    }
    .month-worktype-chart-total {
        font-size: 1.2rem;
        font-weight: 700;
        line-height: 1.1;
        color: #212529;
    }
    .month-worktype-chart-total-label {
        font-size: 0.72rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #868e96;
    }
    .month-worktype-chart-empty {
        min-height: 8.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-size: 0.9rem;
    }

    /* Custom HTML Tooltip (z-index 1050 to always display above canvas & center label) */
    .chartjs-custom-tooltip {
        position: absolute;
        z-index: 1050;
        pointer-events: none;
        background: #ffffff;
        color: #0f172a;
        border-radius: 8px;
        padding: 8px 10px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        font-size: 0.78rem;
        box-shadow: 0 4px 14px rgba(15, 23, 42, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08);
        border: 1px solid #cbd5e1;
        transition: opacity 0.15s ease;
        white-space: nowrap;
    }
    .chartjs-custom-tooltip-title {
        font-weight: 700;
        font-size: 0.8rem;
        color: #0f172a;
        margin-bottom: 3px;
        line-height: 1.2;
    }
    .chartjs-custom-tooltip-item {
        display: flex;
        align-items: center;
        gap: 6px;
        line-height: 1.2;
    }
    .chartjs-custom-tooltip-swatch {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 2px;
        flex-shrink: 0;
    }
    .chartjs-custom-tooltip-text {
        font-size: 0.78rem;
        color: #334155;
    }
    .chartjs-custom-tooltip::before {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        border-style: solid;
        pointer-events: none;
    }
    .chartjs-custom-tooltip[data-y-align="top"]::before {
        top: -6px;
        left: var(--caret-x, 50%);
        transform: translateX(-50%);
        border-width: 0 6px 6px 6px;
        border-color: transparent transparent #cbd5e1 transparent;
    }
    .chartjs-custom-tooltip[data-y-align="bottom"]::before {
        bottom: -6px;
        left: var(--caret-x, 50%);
        transform: translateX(-50%);
        border-width: 6px 6px 0 6px;
        border-color: #cbd5e1 transparent transparent transparent;
    }
    .chartjs-custom-tooltip[data-y-align="center"][data-x-align="left"]::before {
        left: -6px;
        top: 50%;
        transform: translateY(-50%);
        border-width: 6px 6px 6px 0;
        border-color: transparent #cbd5e1 transparent transparent;
    }
    .chartjs-custom-tooltip[data-y-align="center"][data-x-align="right"]::before {
        right: -6px;
        top: 50%;
        transform: translateY(-50%);
        border-width: 6px 0 6px 6px;
        border-color: transparent transparent transparent #cbd5e1;
    }

    /* Dark mode */
    [data-theme="dark"] .chartjs-custom-tooltip {
        background: rgba(24, 28, 36, 0.95);
        color: #f8f9fa;
        border: 1px solid rgba(255, 255, 255, 0.12);
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
    }
    [data-theme="dark"] .chartjs-custom-tooltip-title {
        color: #ffffff;
    }
    [data-theme="dark"] .chartjs-custom-tooltip-text {
        color: #f1f5f9;
    }
    [data-theme="dark"] .chartjs-custom-tooltip[data-y-align="top"]::before {
        border-color: transparent transparent rgba(24, 28, 36, 0.95) transparent;
    }
    [data-theme="dark"] .chartjs-custom-tooltip[data-y-align="bottom"]::before {
        border-color: rgba(24, 28, 36, 0.95) transparent transparent transparent;
    }
    [data-theme="dark"] .chartjs-custom-tooltip[data-y-align="center"][data-x-align="left"]::before {
        border-color: transparent rgba(24, 28, 36, 0.95) transparent transparent;
    }
    [data-theme="dark"] .chartjs-custom-tooltip[data-y-align="center"][data-x-align="right"]::before {
        border-color: transparent transparent transparent rgba(24, 28, 36, 0.95);
    }
    [data-theme="dark"] .time-progress-title {
        color: #cbd5e1 !important;
    }
    [data-theme="dark"] .month-worktype-chart-total {
        color: #f1f5f9 !important;
    }
    [data-theme="dark"] .month-worktype-chart-total-label {
        color: #94a3b8 !important;
    }
    [data-theme="dark"] .month-worktype-chart-empty {
        color: #94a3b8 !important;
    }
</style>